const TEAM_NAMES = new Set(['red', 'blue']);

function normalizeTeam(value) {
  if (value === null || value === undefined) return null;
  const team = String(value).trim().toLowerCase();
  return TEAM_NAMES.has(team) ? team : null;
}

function playerName(value) {
  if (typeof value === 'string') return value.trim();
  if (!value || typeof value !== 'object') return '';
  if (typeof value.name === 'string') return value.name.trim();
  if (typeof value.nick === 'string') return value.nick.trim();
  if (typeof value.player === 'string') return value.player.trim();
  if (value.player && typeof value.player === 'object') return playerName(value.player);
  return '';
}

function pendingShotForTeam(pendingShots, shots, matchIndex, scoringTeam) {
  const expectedTeam = normalizeTeam(scoringTeam);
  if (!expectedTeam || !Array.isArray(pendingShots)) return null;
  for (let index = pendingShots.length - 1; index >= 0; index -= 1) {
    const pending = pendingShots[index];
    if (!pending || (matchIndex !== null && matchIndex !== undefined && pending.matchIndex !== matchIndex)) continue;
    if (normalizeTeam(pending.team) !== expectedTeam) continue;
    const shot = Array.isArray(shots) ? shots[pending.shotIndex] : null;
    if (!shot) continue;
    // A pending entry is removed when a shot is saved or attributed to a goal.
    // Keep this guard for callers that pass a sidecar reconstructed from an
    // already-resolved match.
    if (shot.outcome && String(shot.outcome).toLowerCase() !== 'pending') continue;
    const shooter = playerName(shot.player ?? shot.shooter ?? shot.nick);
    if (!shooter) continue;
    return {
      pending,
      shot,
      shotIndex: pending.shotIndex,
      scorer: shooter,
    };
  }
  return null;
}

/**
 * Attribute a replay goal using the same evidence order as the room script:
 * an on-target attacking shot wins over a later defensive touch, and a player
 * is only labelled with an own goal when no attacking shot is still pending.
 *
 * The native replay parser cannot observe every continuous ball contact. The
 * pending-shot queue therefore acts as the reliable shot/deflection link;
 * latestAction is used only as a fallback for direct kicks or own goals.
 */
export function resolveReplayGoalAttribution({
  scoringTeam,
  pendingShots = [],
  shots = [],
  matchIndex = null,
  latestAction = null,
} = {}) {
  const expectedTeam = normalizeTeam(scoringTeam);
  const pending = pendingShotForTeam(pendingShots, shots, matchIndex, scoringTeam);
  if (pending) {
    const latestName = playerName(latestAction);
    const latestTeam = normalizeTeam(latestAction?.team);
    const recordedDeflection = playerName(pending.pending?.lastDefensiveTouch) || playerName(pending.shot?.deflectionPlayer);
    const recordedDeflectionTeam = normalizeTeam(pending.pending?.lastDefensiveTeam || pending.shot?.deflectionTeam);
    const isDeflection = latestName && (
      latestName !== pending.scorer || (latestTeam && latestTeam !== expectedTeam)
    );
    return {
      scorer: pending.scorer,
      ownGoal: false,
      source: 'shot',
      shot: pending.shot,
      shotIndex: pending.shotIndex,
      deflectionPlayer: isDeflection ? latestName : recordedDeflection || null,
      deflectionTeam: isDeflection ? latestTeam : recordedDeflectionTeam || null,
    };
  }

  const fallbackName = playerName(latestAction);
  const fallbackTeam = normalizeTeam(latestAction?.team);
  if (!fallbackName) {
    return {
      scorer: null,
      ownGoal: false,
      source: 'unknown',
      shot: null,
      shotIndex: null,
      deflectionPlayer: null,
      deflectionTeam: fallbackTeam,
    };
  }

  if (expectedTeam && fallbackTeam === expectedTeam) {
    return {
      scorer: fallbackName,
      ownGoal: false,
      source: latestAction?.kind === 'touch' ? 'touch' : 'kick',
      shot: latestAction?.shot || null,
      shotIndex: null,
      deflectionPlayer: null,
      deflectionTeam: fallbackTeam,
    };
  }

  // A final defensive kick/touch that sends the ball into its own net is the
  // only fallback case that should become an own goal. A goalkeeper touching
  // an attacker's pending shot never reaches this branch.
  return {
    scorer: fallbackName,
    ownGoal: Boolean(expectedTeam && fallbackTeam && fallbackTeam !== expectedTeam),
    source: 'own-goal',
    shot: latestAction?.shot || null,
    shotIndex: null,
    deflectionPlayer: fallbackName,
    deflectionTeam: fallbackTeam,
  };
}

export { normalizeTeam as normalizeReplayGoalTeam };
